import { ActionType, BallAction, FrameStencil, MarkerType, MovementType, StencilType } from "../../../types";
import { sliceItemUpdate } from "../../splice-item";
import { findClosestStencil } from "../find-closest";
import {
  getBallActions,
  getBallActionsNoNext,
  getBallMarkers,
  getBallMarkersNoMovement,
  getPlayerActions,
  getPlayerMarkers,
  getPossessionActions,
  getPossessionActionsNoNextBall,
  isBallOrPossessionAction,
} from "../frame-stencil-filters";
import { getDurationFromAction } from "../get-duration-from-action";

export interface CheckBallActionStartPointProps {
  action: Omit<BallAction, "startTime" | "endTime"> & Partial<Pick<BallAction, "startTime" | "endTime">>;
  stencils: FrameStencil[];
}

export const checkBallActionStartPoint = ({ action, stencils }: CheckBallActionStartPointProps) => {
  const newActionDuration = getDurationFromAction(action);

  // get all balls that don't already have a movement, ballActions that don't have a nextBallAction,
  // and playerActions that aren't on a ball's playerActionStart
  const ballActions = getBallActions(stencils);
  const playerActions = getPlayerActions(stencils).filter(
    (a) => !ballActions.some((b) => b.playerActionStart === a.id)
  );
  const stencilsToCheck = [
    ...getPlayerMarkers(stencils).filter((p) => {
      const possessionActions = getPossessionActions(stencils).map((a) => a.id);
      return !possessionActions.includes(p.movements[0]?.id || "");
    }),
    ...getBallMarkersNoMovement(stencils),
    ...getBallActionsNoNext(stencils),
    ...getPossessionActionsNoNextBall(stencils),
    ...playerActions,
  ];

  // find closest stencil to startPoint of new ballMovement
  const closestStencilToStart = findClosestStencil({
    target: { id: action.id, point: action.pos },
    stencils: stencilsToCheck,
    stencilsPointAccessor: (s) => (s.stencilType === StencilType.MARKER ? s.pos : s.endPos),
  });

  const newActionStartTime: number = ((): number => {
    if (!closestStencilToStart) return action?.startTime || 0;

    switch (closestStencilToStart.stencilType) {
      case StencilType.MARKER: {
        switch (closestStencilToStart.markerType) {
          case MarkerType.PLAYER: {
            const passEndTimesToPlayer = getBallActions(stencils)
              .filter((s) => s.nextPlayer === closestStencilToStart.id)
              .map((pass) => pass.endTime);

            return Math.max(...passEndTimesToPlayer, action?.startTime || 0);
          }

          case MarkerType.BALL: {
            return action?.startTime || 0;
          }

          default:
            return action?.startTime || 0;
        }
      }

      case StencilType.ACTION: {
        return Math.max(closestStencilToStart.endTime, action?.startTime || 0);
      }
    }
  })();

  const newActionEndTime = newActionStartTime + newActionDuration;

  const actionWithTimes = { ...action, startTime: newActionStartTime, endTime: newActionEndTime } as BallAction;

  const updatedBallAction = ((): BallAction => {
    switch (closestStencilToStart?.stencilType) {
      case StencilType.MARKER: {
        switch (closestStencilToStart.markerType) {
          case MarkerType.PLAYER:
            return { ...actionWithTimes, prevPlayer: closestStencilToStart.id };

          case MarkerType.BALL:
            return { ...actionWithTimes, prevPlayer: closestStencilToStart.associatedPlayer };

          default:
            return actionWithTimes;
        }
      }

      case StencilType.ACTION: {
        const playerOnPrevAction = getPlayerMarkers(stencils).find((s) =>
          s.movements.map((m) => m.id).includes(closestStencilToStart.id)
        );

        const passWithPrevPlayer = {
          ...actionWithTimes,
          ...(playerOnPrevAction && { prevPlayer: playerOnPrevAction.id }),
        };
        return {
          ...passWithPrevPlayer,
          ...(closestStencilToStart.actionType === ActionType.PLAYER && {
            playerActionStart: closestStencilToStart.id,
          }),
        };
      }

      default:
        return actionWithTimes;
    }
  })();

  const stencilsWithUpdatedPrevStencil: FrameStencil[] = ((): FrameStencil[] => {
    if (!closestStencilToStart) return stencils;

    switch (closestStencilToStart.stencilType) {
      case StencilType.MARKER: {
        switch (closestStencilToStart.markerType) {
          case MarkerType.PLAYER: {
            const ballOnPlayer = getBallMarkers(stencils).find(
              (ball) => ball.associatedPlayer === closestStencilToStart.id
            );

            const stencilsWithBallMovement = ballOnPlayer
              ? sliceItemUpdate({
                  arr: stencils,
                  item: {
                    ...ballOnPlayer,
                    movements: [{ id: closestStencilToStart.id, movementType: MovementType.POSSESSION, startTime: 0 }],
                  },
                })
              : stencils;

            const passEndToPlayer = getBallActions(stencilsWithBallMovement).find(
              (s) => s.nextPlayer === closestStencilToStart.id
            );
            return passEndToPlayer
              ? sliceItemUpdate({
                  arr: stencilsWithBallMovement,
                  item: { ...passEndToPlayer, nextBallAction: updatedBallAction.id },
                })
              : stencilsWithBallMovement;
          }

          case MarkerType.BALL:
            return sliceItemUpdate({
              arr: stencils,
              item: {
                ...closestStencilToStart,
                movements: [
                  { id: closestStencilToStart.associatedPlayer, movementType: MovementType.POSSESSION, startTime: 0 },
                ],
              },
            });

          default:
            return stencils;
        }
      }

      case StencilType.ACTION: {
        return isBallOrPossessionAction(closestStencilToStart)
          ? sliceItemUpdate({
              arr: stencils,
              item: { ...closestStencilToStart, nextBallAction: updatedBallAction.id },
            })
          : stencils;
      }
    }
  })();

  return { action: updatedBallAction, stencils: stencilsWithUpdatedPrevStencil };
};
